import type { Proposal, ProposalCandidate, ProposalDecision } from '@fall26/shared'
import { useState } from 'react'
import { defaultCheckedFields, toggleField } from './diff-selection.js'
import { Action, Fact, Facts, FIELD_LABEL, fieldText, KindBadge } from './parts.js'

/**
 * One card per feed item, whatever the queue calls it.
 *
 * `add` and `link` are the same screen on purpose: an `add` is a proposal whose
 * candidate list happens to be empty. Splitting them into two flows would mean
 * a feed item that matched nothing looked structurally different from one that
 * matched weakly, and the weak-match case is exactly where I need the same
 * layout and the same buttons rather than a new decision to learn.
 */
export function ProposalCard({
  proposal,
  onDecide,
  busy,
}: {
  proposal: Proposal
  onDecide: (decision: ProposalDecision, description: string) => void
  busy: boolean
}) {
  return (
    <article className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-3.5">
      <Header proposal={proposal} />
      {proposal.kind === 'stale' ? (
        <Stale proposal={proposal} onDecide={onDecide} busy={busy} />
      ) : proposal.kind === 'update' ? (
        <Update proposal={proposal} onDecide={onDecide} busy={busy} />
      ) : (
        <AddOrLink proposal={proposal} onDecide={onDecide} busy={busy} />
      )}
    </article>
  )
}

function Header({ proposal }: { proposal: Proposal }) {
  const title = proposal.feedItem?.title ?? proposal.localItem?.title ?? proposal.externalId
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="tabular text-[10px] uppercase tracking-[0.1em] text-[var(--text-subtle)]">
          {proposal.courseId ?? 'no course'}
        </span>
        <KindBadge kind={proposal.kind} />
        {proposal.status === 'skipped' && (
          <span className="tabular rounded-full bg-[var(--surface-sunken)] px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-[var(--text-subtle)]">
            snoozed
          </span>
        )}
        {proposal.touchesOverridden && (
          <span className="tabular rounded-full bg-[var(--accent-wash)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--accent)]">
            you edited this
          </span>
        )}
      </div>
      <h3 className="mt-1 text-[15px] font-medium leading-snug text-[var(--text)]">{title}</h3>
    </div>
  )
}

// ---------------------------------------------------------------------------
// add / link
// ---------------------------------------------------------------------------

function AddOrLink({
  proposal,
  onDecide,
  busy,
}: {
  proposal: Proposal
  onDecide: (decision: ProposalDecision, description: string) => void
  busy: boolean
}) {
  // Nothing is preselected. Picking a candidate for me would be exactly the
  // heuristic auto-merge this whole queue exists to avoid.
  const [picked, setPicked] = useState<number | null>(null)
  const feed = proposal.feedItem

  return (
    <>
      <Facts>
        {feed?.dueDate ? (
          <Fact label="Canvas">
            <span className="tabular text-[var(--text)]">{feed.dueDisplay}</span>
            {feed.dueAtUtcEstimated && (
              // An all-day entry in the feed. Saying so matters: the difference
              // between "due at 11:59 PM" and "Canvas didn't say" is the
              // difference between a deadline and a guess.
              <span className="ml-1.5 text-[11px] text-[var(--unknown)]">all day — time assumed</span>
            )}
          </Fact>
        ) : (
          <Fact label="Canvas">
            <span className="text-[var(--unknown)]">no date in the feed</span>
          </Fact>
        )}
        {feed?.detail && <Fact label="Detail">{feed.detail}</Fact>}
      </Facts>

      {proposal.candidates.length > 0 && (
        <div className="mt-3 rounded-[var(--radius)] border border-[var(--soon)]/30 bg-[var(--soon-wash)] p-2.5">
          <p className="text-[12px] leading-snug text-[var(--text)]">
            {proposal.candidates.length === 1
              ? 'This may already be one of your items.'
              : `This may already be one of ${proposal.candidates.length} of your items.`}{' '}
            Linking maps the feed item onto the row you pick — adding creates a second one.
          </p>
          <ul className="mt-2 space-y-1.5">
            {proposal.candidates.map((candidate) => (
              <Candidate
                key={candidate.itemId}
                candidate={candidate}
                picked={picked === candidate.itemId}
                onPick={() => setPicked(picked === candidate.itemId ? null : candidate.itemId)}
              />
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {proposal.candidates.length > 0 && (
          <Action
            // Quiet until a candidate is picked. A greyed-out solid button reads
            // as the one you are meant to press, which is the wrong nudge on the
            // screen whose entire job is not choosing for me.
            tone={picked === null ? 'quiet' : 'primary'}
            busy={busy}
            disabled={picked === null}
            onClick={() => picked !== null && onDecide({ kind: 'link', localItemId: picked }, 'Linked')}
          >
            {picked === null ? 'Pick one to link' : 'Link'}
          </Action>
        )}
        <Action
          tone={proposal.candidates.length > 0 ? 'quiet' : 'primary'}
          busy={busy}
          onClick={() => onDecide({ kind: 'add-new' }, 'Added')}
        >
          Add as new
        </Action>
        <Action busy={busy} onClick={() => onDecide({ kind: 'skip' }, 'Snoozed')}>
          Later
        </Action>
        <Action busy={busy} onClick={() => onDecide({ kind: 'ignore-once' }, 'Ignored')}>
          Not this
        </Action>
        <Action busy={busy} onClick={() => onDecide({ kind: 'ignore-forever' }, 'Ignored for good')}>
          Never
        </Action>
      </div>
    </>
  )
}

function Candidate({
  candidate,
  picked,
  onPick,
}: {
  candidate: ProposalCandidate
  picked: boolean
  onPick: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        aria-pressed={picked}
        className={`tap flex w-full items-start gap-2 rounded-[var(--radius)] border p-2 text-left transition-colors ${
          picked
            ? 'border-[var(--accent-line)] bg-[var(--accent-wash)]'
            : 'border-[var(--border)] bg-[var(--surface)]'
        }`}
      >
        <span
          aria-hidden
          className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ${
            picked ? 'border-[var(--accent)] bg-[var(--accent)]' : 'border-[var(--border-strong)]'
          }`}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] leading-snug text-[var(--text)]">{candidate.title}</span>
          <span className="tabular mt-0.5 block text-[11px] text-[var(--text-subtle)]">
            {candidate.dueDisplay || 'no date'} · from {candidate.source} ·{' '}
            {Math.round(candidate.score * 100)}% match
          </span>
          {candidate.daysApart !== null && candidate.daysApart !== 0 && (
            // The Exam 2 case, called out rather than buried in a match score:
            // Canvas says one date and the syllabus says another, seven days
            // apart, and linking them accepts Canvas's.
            <span className="tabular mt-0.5 block text-[11px] font-semibold text-[var(--soon)]">
              {Math.abs(candidate.daysApart)} day{Math.abs(candidate.daysApart) === 1 ? '' : 's'} apart from
              what Canvas says
            </span>
          )}
        </span>
      </button>
    </li>
  )
}

// ---------------------------------------------------------------------------
// update
// ---------------------------------------------------------------------------

function Update({
  proposal,
  onDecide,
  busy,
}: {
  proposal: Proposal
  onDecide: (decision: ProposalDecision, description: string) => void
  busy: boolean
}) {
  /*
   * A field I authored starts UNCHECKED. That is the single most important
   * default on this screen: an override exists precisely because I decided the
   * feed was wrong about something, and "Apply" arriving pre-ticked would undo
   * that decision with a tap I made for the other three fields.
   */
  const [checked, setChecked] = useState<ReadonlySet<string>>(() => defaultCheckedFields(proposal.diffs))

  const toggle = (field: string) => setChecked((current) => toggleField(current, field))

  const fields = proposal.diffs.filter((d) => checked.has(d.field)).map((d) => d.field)

  return (
    <>
      <p className="mt-1.5 text-[12px] text-[var(--text-muted)]">
        Canvas changed {proposal.diffs.length} field{proposal.diffs.length === 1 ? '' : 's'} on an item you
        already have.
      </p>

      <ul className="mt-2 space-y-1.5">
        {proposal.diffs.map((diff) => {
          const on = checked.has(diff.field)
          return (
            <li key={diff.field}>
              <button
                type="button"
                onClick={() => toggle(diff.field)}
                role="checkbox"
                aria-checked={on}
                className={`tap flex w-full items-start gap-2.5 rounded-[var(--radius)] border p-2 text-left transition-colors ${
                  diff.overridden
                    ? 'border-[var(--accent-line)] bg-[var(--accent-wash)]'
                    : 'border-[var(--border)] bg-[var(--surface-sunken)]'
                }`}
              >
                <span
                  aria-hidden
                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-2 ${
                    on ? 'border-[var(--done)] bg-[var(--done)]' : 'border-[var(--border-strong)]'
                  }`}
                >
                  {on && (
                    <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden>
                      <path
                        d="M2.5 6.2 4.7 8.4 9.5 3.6"
                        fill="none"
                        stroke="var(--accent-contrast)"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="tabular block text-[10px] uppercase tracking-[0.1em] text-[var(--text-subtle)]">
                    {FIELD_LABEL[diff.field] ?? diff.field}
                    {diff.overridden && (
                      <span className="ml-1.5 text-[var(--accent)]">· yours, unchecked on purpose</span>
                    )}
                  </span>
                  <span className="tabular mt-0.5 block text-[12px] leading-snug">
                    <span className="text-[var(--text-muted)] line-through">{fieldText(diff.mineDisplay)}</span>
                    <span className="mx-1.5 text-[var(--text-subtle)]">→</span>
                    <span className="text-[var(--text)]">{fieldText(diff.theirsDisplay)}</span>
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <Action
          tone="primary"
          busy={busy}
          disabled={fields.length === 0}
          onClick={() => onDecide({ kind: 'apply-fields', fields }, 'Applied')}
        >
          {fields.length === 0 ? 'Nothing checked' : `Apply ${fields.length}`}
        </Action>
        <Action busy={busy} onClick={() => onDecide({ kind: 'skip' }, 'Snoozed')}>
          Later
        </Action>
        <Action busy={busy} onClick={() => onDecide({ kind: 'reject' }, 'Rejected')}>
          Keep mine
        </Action>
        <Action busy={busy} onClick={() => onDecide({ kind: 'ignore-forever' }, 'Ignored for good')}>
          Never
        </Action>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// stale
// ---------------------------------------------------------------------------

function Stale({
  proposal,
  onDecide,
  busy,
}: {
  proposal: Proposal
  onDecide: (decision: ProposalDecision, description: string) => void
  busy: boolean
}) {
  // Delete is the only irreversible button in the queue, so it asks twice.
  const [confirming, setConfirming] = useState(false)
  const local = proposal.localItem

  return (
    <>
      <p className="mt-1.5 text-[12px] leading-snug text-[var(--text-muted)]">
        Canvas has stopped carrying this. That usually means the instructor removed it — but a feed also drops
        items for reasons that have nothing to do with the work, so nothing happens until you say so.
      </p>

      <Facts>
        {local && (
          <Fact label="Yours">
            <span className="tabular text-[var(--text)]">{local.dueDisplay || 'no date'}</span>
            {local.done && <span className="ml-1.5 text-[var(--done)]">already done</span>}
          </Fact>
        )}
      </Facts>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <Action tone="primary" busy={busy} onClick={() => onDecide({ kind: 'keep-as-is' }, 'Kept')}>
          Keep it
        </Action>
        <Action busy={busy} onClick={() => onDecide({ kind: 'mark-done' }, 'Marked done')}>
          Mark done
        </Action>
        <Action busy={busy} onClick={() => onDecide({ kind: 'skip' }, 'Snoozed')}>
          Later
        </Action>
        {confirming ? (
          <Action tone="danger" busy={busy} onClick={() => onDecide({ kind: 'delete' }, 'Deleted')}>
            Delete for good?
          </Action>
        ) : (
          <Action tone="danger" busy={busy} onClick={() => setConfirming(true)}>
            Delete
          </Action>
        )}
      </div>
    </>
  )
}
